import { useState } from 'react';
import { Calculator, Info } from 'lucide-react';
import { cn, putClientCalculationSettingsByClientIdRequest, selectClient } from '..';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { Button } from './ui/button';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { useDispatch, useSelector } from 'react-redux';
import { Badge } from './ui/badge';

const roiDisplayOptions = [
  {
    value: 'total',
    label: 'Total ROI',
    description:
      'Total ROI shows the overall return by comparing the total paid out to what was paid in. (Total Payout - Cost) / Cost',
  },
  {
    value: 'internalRateOfReturn',
    label: 'Internal Rate of Return',
    display: 'IRR',
    description: `Internal Rate of Return (IRR) is the interest rate at which the present value of all future cash flows (both positive and negative) equals zero, or in simpler terms, it's the annual rate of growth that an investment is expected to generate.`,
  },
  {
    value: 'compoundAnnualGrowthRate',
    label: 'Compound Annual Growth Rate',
    display: 'CAGR',
    description:
      'Compound Annualized Growth Rate (CAGR) provides a "smoothed out" growth rate that shows what the annual rate would have been if growth had occurred evenly over the entire period.',
  },
] as const;

// Reusable InfoTooltip component
const InfoTooltip = ({ content }: { content: React.ReactNode }) => (
  <TooltipProvider delayDuration={0}>
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex">
          <Info
            strokeWidth={1.5}
            size={14}
            className="flex-shrink-0 text-gray-600"
          />
        </span>
      </TooltipTrigger>
      <TooltipContent className="m-1 w-auto max-w-80 whitespace-normal p-2 text-sm font-normal">
        {content}
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

export function ReturnOnInvestmentCalculationOptions({
  discountRate,
  calculationType,
  onChange,
  className,
  isButtonStyle = false,
}: {
  discountRate: number | undefined | null;
  calculationType: ReturnOnInvestmentCalculationType;
  onChange: (value: ReturnOnInvestmentCalculationType) => void;
  className?: string;
  isButtonStyle?: boolean;
}) {
  const dispatch = useDispatch();
  const { clientId } = useSelector(selectClient);
  const [open, setOpen] = useState(false);
  const [selectedRoiValue, setSelectedRoiValue] =
    useState<ReturnOnInvestmentCalculationType>(calculationType);
  const [discountRateState, setDiscountRateState] = useState<number | undefined | null>(
    discountRate,
  );
  const [error, setError] = useState<string | null>(null);

  const handleApply = () => {
    setError(null);

    const needsDiscountRate = selectedRoiValue === 'compoundAnnualGrowthRate';

    if (needsDiscountRate && discountRateState === undefined) {
      setError('Discount rate is required for CAGR calculation');
      return;
    }

    onChange(selectedRoiValue);
    dispatch(
      putClientCalculationSettingsByClientIdRequest({
        clientId,
        clientCalculationSettings: {
          returnOnInvestmentCalculationType: selectedRoiValue,
          discountRate: discountRateState ?? null,
        },
      }),
    );
    setOpen(false);
  };

  const handleDiscountRateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '') {
      setDiscountRateState(undefined);
    } else {
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        setDiscountRateState(numValue);
      }
    }
  };

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
    >
      <PopoverTrigger asChild>
        <Button
          variant={isButtonStyle ? 'outline' : 'link'}
          role="combobox"
          size="sm"
          aria-expanded={open}
          className={cn(
            isButtonStyle
              ? 'flex items-center justify-center'
              : 'flex items-center justify-start text-xs text-gray-500 hover:text-gray-900',
            className,
          )}
          onClick={e => {
            e.stopPropagation();
            setOpen(true);
          }}
        >
          <Calculator
            strokeWidth={1.5}
            size={16}
            className={isButtonStyle ? 'mr-1' : ''}
          />
          {isButtonStyle && 'ROI Calculator'}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-5 md:w-80">
        <div className="grid gap-4">
          <div className="flex items-baseline justify-between space-y-2 pb-1">
            <h4 className="font-medium leading-none">ROI Calculation Type</h4>
            <Badge variant="secondary">Beta</Badge>
          </div>
          <div className="grid gap-2 space-y-2">
            <div className="flex flex-col gap-2">
              <RadioGroup
                value={selectedRoiValue}
                onValueChange={value =>
                  setSelectedRoiValue(value as ReturnOnInvestmentCalculationType)
                }
              >
                {roiDisplayOptions.map(option => (
                  <div
                    key={option.value}
                    className="flex items-center space-x-2"
                  >
                    <RadioGroupItem
                      value={option.value}
                      id={option.value}
                    />
                    <Label
                      htmlFor={option.value}
                      className="flex items-baseline gap-1 text-xs md:text-sm"
                    >
                      {option.label}
                      <InfoTooltip content={option.description} />
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>
            <div className="flex flex-col gap-2 pt-1">
              <div className="flex items-baseline gap-1">
                <Label htmlFor="discountRate">Discount Rate</Label>
                <InfoTooltip content="The discount rate affects calculations only when there are future investment payments rather than a single upfront cost. When investments occur over time (like ongoing premiums), a higher discount rate means those future payments are valued less in today's terms. For one-time investments, the discount rate has no impact since there are no future payments to discount." />
              </div>
              <Input
                id="discountRate"
                value={discountRateState ?? ''}
                placeholder="Enter number (e.g. 5.5)"
                onChange={handleDiscountRateChange}
                disabled={selectedRoiValue !== 'compoundAnnualGrowthRate'}
                className="h-8"
                type="number"
                step="0.1"
              />
            </div>
            {error && <div className="whitespace-normal text-sm text-red-500">{error}</div>}
            <Button
              size="sm"
              className="ml-auto w-24"
              onClick={handleApply}
            >
              Apply
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
